import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, LogOut, Download, FileText, Building2, Briefcase, 
  Heart, Bitcoin, Home, BarChart3, FolderOpen, Loader2, Eye,
  User, ChevronDown, ChevronUp, Lock, Archive
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import BrandMark from '../../components/BrandMark';
import PartnerMark from '../../components/PartnerMark';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Category icons and labels
const CATEGORY_CONFIG = {
  banks: { icon: Building2, label: 'Bank Accounts', color: 'text-blue-400' },
  wills: { icon: FileText, label: 'Will Documents', color: 'text-purple-400' },
  mutual_funds: { icon: Briefcase, label: 'Mutual Funds', color: 'text-green-400' },
  insurance: { icon: Heart, label: 'Insurance Policies', color: 'text-red-400' },
  crypto: { icon: Bitcoin, label: 'Cryptocurrency', color: 'text-yellow-400' },
  property: { icon: Home, label: 'Properties', color: 'text-cyan-400' },
  demat: { icon: BarChart3, label: 'Demat Accounts', color: 'text-orange-400' },
  others: { icon: FolderOpen, label: 'Other Documents', color: 'text-slate-400' }
};

const TrustedDashboard = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  const [profile, setProfile] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [categories, setCategories] = useState({});
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);

  const trustedToken = localStorage.getItem('trustedToken');
  const axiosConfig = {
    headers: { Authorization: `Bearer ${trustedToken}` }
  };

  // Check authentication
  useEffect(() => {
    if (!trustedToken) {
      navigate('/trusted/login');
    }
  }, [trustedToken, navigate]);

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!trustedToken) return;
      
      setIsLoading(true);
      try {
        const [profileRes, clientRes, entriesRes] = await Promise.all([
          axios.get(`${API}/trusted/profile`, axiosConfig),
          axios.get(`${API}/trusted/client-info`, axiosConfig),
          axios.get(`${API}/trusted/shared-entries`, axiosConfig)
        ]);

        setProfile(profileRes.data.trusted_contact);
        setClientInfo(clientRes.data.client);
        setPartnerInfo(clientRes.data.partner);
        setCategories(entriesRes.data.categories || {});
      } catch (error) {
        console.error('Failed to fetch data:', error);
        if (error.response?.status === 401) {
          localStorage.removeItem('trustedToken');
          localStorage.removeItem('trustedName');
          localStorage.removeItem('trustedEmail');
          navigate('/trusted/login');
        } else {
          toast.error('Failed to load data');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [trustedToken]);

  // Handle logout — redirect to the source login page
  const handleLogout = async () => {
    const token = localStorage.getItem('trustedToken');
    if (token) {
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) { /* ignore */ }
    }
    const loginSource = localStorage.getItem('trustedLoginSource');
    localStorage.removeItem('trustedToken');
    localStorage.removeItem('trustedAccessToken');
    localStorage.removeItem('trustedRefreshToken');
    localStorage.removeItem('trustedName');
    localStorage.removeItem('trustedEmail');
    localStorage.removeItem('trustedLoginSource');
    
    // Use saved login source, but skip reset-password/setup paths
    let redirectPath = '/trusted/login';
    if (loginSource && !loginSource.includes('reset-password') && !loginSource.includes('trusted_setup')) {
      redirectPath = loginSource;
    } else if (partnerInfo?.subdomain) {
      redirectPath = `https://${partnerInfo.subdomain}.willkart.ai`;
    }
    navigate(redirectPath);
    toast.success('Logged out successfully');
  };

  // Handle file download
  const handleDownloadFile = async (entryId, file) => {
    setDownloadingFile(file.id);
    try {
      const response = await axios.get(
        `${API}/trusted/entry/${entryId}/file/${file.id}/download`,
        {
          ...axiosConfig,
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.original_name || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded');
    } catch (error) {
      toast.error('Failed to download file');
    } finally {
      setDownloadingFile(null);
    }
  };

  // Handle download all
  const handleDownloadAll = async () => {
    setIsDownloadingAll(true);
    try {
      const response = await axios.get(
        `${API}/trusted/download-all`,
        {
          ...axiosConfig,
          responseType: 'blob'
        }
      );

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${clientInfo?.name || 'Client'} TP Life Vault.zip`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Life Vault downloaded successfully!');
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download Life Vault');
    } finally {
      setIsDownloadingAll(false);
    }
  };

  // Get display value for an entry - prioritize save_as, then category-specific fields
  const getEntryDisplayName = (category, entry) => {
    // First check if save_as exists (user-defined name)
    if (entry.save_as) return entry.save_as;
    
    const data = entry.data || {};
    
    // Category-specific field mapping
    switch (category) {
      case 'banks':
        return data.institution_name || data.bank_name || 'Bank Account';
      case 'mutual_funds':
        return data.advisor_name || data.institution_name || data.fund_name || 'Mutual Fund';
      case 'insurance':
        return data.policy_provider || data.company_name || data.policy_name || 'Insurance Policy';
      case 'crypto':
        return data.exchange_name || data.wallet_name || 'Crypto Wallet';
      case 'property':
        return data.property_name || data.address || data.location || 'Property';
      case 'demat':
        return data.broker_name || data.depository || 'Demat Account';
      case 'wills':
        return data.title || data.description || 'Will Document';
      case 'others':
        return data.title || data.description || data.name || 'Other Document';
      default:
        return data.name || data.title || data.description || 'Entry';
    }
  };

  // Render entry details (read-only) - Show ALL fields from the entry data
  const renderEntryDetails = (category, entry) => {
    const data = entry.data || {};
    const fields = [];
    
    // Field label mapping for better display names
    const labelMap = {
      // Banks
      institution_name: 'Bank Name',
      bank_name: 'Bank Name',
      account_number: 'Account Number',
      account_type: 'Account Type',
      ifsc_code: 'IFSC Code',
      branch: 'Branch',
      joint_holder_1: 'Joint Holder 1',
      joint_holder_2: 'Joint Holder 2',
      nominee_1: 'Nominee 1',
      nominee_2: 'Nominee 2',
      nominee_3: 'Nominee 3',
      contact_person_name: 'Contact Person',
      contact_person_mobile: 'Contact Mobile',
      
      // Mutual Funds
      advisor_name: 'Advisor Name',
      arn_number: 'ARN Number',
      fund_name: 'Fund Name',
      folio_number: 'Folio Number',
      nominee_name: 'Nominee Name',
      
      // Insurance
      policy_provider: 'Insurance Provider',
      company_name: 'Company Name',
      policy_name: 'Policy Name',
      policy_number: 'Policy Number',
      policy_type: 'Policy Type',
      sum_assured: 'Sum Assured',
      premium_amount: 'Premium Amount',
      nominee_contact: 'Nominee Contact',
      maturity_date: 'Maturity Date',
      
      // Crypto
      exchange_name: 'Exchange',
      wallet_name: 'Wallet Name',
      wallet_type: 'Wallet Type',
      wallet_address: 'Wallet Address',
      exchange_account: 'Exchange Account',
      recovery_phrase_hint: 'Recovery Hint',
      
      // Property
      property_name: 'Property Name',
      property_type: 'Property Type',
      address: 'Address',
      location: 'Location',
      survey_number: 'Survey Number',
      registration_number: 'Registration Number',
      area: 'Area',
      ownership_type: 'Ownership Type',
      
      // Demat
      broker_name: 'Broker Name',
      depository: 'Depository',
      demat_account_number: 'Demat Account Number',
      dp_id: 'DP ID',
      rm_name: 'RM Name',
      rm_number: 'RM Number',
      
      // Common
      description: 'Description',
      title: 'Title',
      name: 'Name',
      notes: 'Notes',
      sensitive_info: 'Additional Info'
    };
    
    // Iterate through all data fields and add them
    Object.entries(data).forEach(([key, value]) => {
      // Skip empty values, internal fields, and already processed
      if (value === null || value === undefined || value === '' || key.startsWith('_')) {
        return;
      }
      
      // Get display label
      const label = labelMap[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      // Format the value
      let displayValue = value;
      if (typeof value === 'boolean') {
        displayValue = value ? 'Yes' : 'No';
      } else if (typeof value === 'object') {
        displayValue = JSON.stringify(value);
      }
      
      fields.push({ label, value: displayValue });
    });
    
    // Add save_as if present
    if (entry.save_as && !fields.find(f => f.label === 'Saved As')) {
      fields.unshift({ label: 'Saved As', value: entry.save_as });
    }

    return fields;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
      </div>
    );
  }

  const totalEntries = Object.values(categories).flat().length;
  const categoriesWithEntries = Object.entries(categories).filter(([_, entries]) => entries.length > 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <header className="bg-slate-900/80 border-b border-slate-800 sticky top-0 z-50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {partnerInfo?.logo_url ? (
                <img
                  src={partnerInfo.logo_url}
                  alt={partnerInfo.name}
                  className="h-10 object-contain"
                />
              ) : (
                <PartnerMark partnerInfo={partnerInfo} size="md" showName={false} />
              )}
              <div>
                <h1 className="text-xl font-bold text-white">Trusted Access Portal</h1>
                <p className="text-slate-400 text-sm">
                  Viewing documents shared by {clientInfo?.name || 'Client'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="text-white font-medium">{profile?.name}</p>
                <p className="text-slate-400 text-sm">{profile?.email}</p>
              </div>
              <Button
                variant="outline"
                onClick={handleLogout}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="trusted-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Card with Download All */}
        <Card className="bg-slate-900/50 border-slate-800 mb-8">
          <CardContent className="p-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                <div className="p-4 rounded-xl bg-amber-500/10">
                  <Lock className="w-8 h-8 text-amber-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-white">Read-Only Access</h2>
                  <p className="text-slate-400">
                    You have access to view and download <span className="text-amber-400 font-medium">{totalEntries} entries</span> across{' '}
                    <span className="text-amber-400 font-medium">{categoriesWithEntries.length} categories</span>.
                  </p>
                </div>
              </div>
              {totalEntries > 0 && (
                <Button
                  onClick={handleDownloadAll}
                  disabled={isDownloadingAll}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
                  data-testid="download-all-btn"
                >
                  {isDownloadingAll ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Preparing...
                    </>
                  ) : (
                    <>
                      <Archive className="w-5 h-5 mr-2" />
                      Download All
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Categories */}
        {totalEntries === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800">
            <CardContent className="p-12 text-center">
              <Shield className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">No Documents Shared</h3>
              <p className="text-slate-400">
                {clientInfo?.name || 'The client'} hasn't shared any documents with you yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {categoriesWithEntries.map(([category, entries]) => {
              const config = CATEGORY_CONFIG[category] || CATEGORY_CONFIG.others;
              const Icon = config.icon;
              const isExpanded = expandedCategory === category;

              return (
                <Card key={category} className="bg-slate-900/50 border-slate-800 overflow-hidden">
                  {/* Category Header */}
                  <button
                    onClick={() => setExpandedCategory(isExpanded ? null : category)}
                    className="w-full p-4 flex items-center justify-between hover:bg-slate-800/30 transition-colors"
                    data-testid={`category-${category}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg bg-slate-800 ${config.color}`}>
                        <Icon className="w-5 h-5" />
                      </div>
                      <div className="text-left">
                        <h3 className="text-white font-medium">{config.label}</h3>
                        <p className="text-slate-400 text-sm">{entries.length} entries</p>
                      </div>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="w-5 h-5 text-slate-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-slate-400" />
                    )}
                  </button>

                  {/* Entries List */}
                  {isExpanded && (
                    <div className="border-t border-slate-800">
                      {entries.map((entry) => {
                        const isEntryExpanded = expandedEntry === entry.id;
                        const details = renderEntryDetails(category, entry);
                        const files = entry.files || [];

                        return (
                          <div key={entry.id} className="border-b border-slate-800/50 last:border-b-0">
                            {/* Entry Header */}
                            <button
                              onClick={() => setExpandedEntry(isEntryExpanded ? null : entry.id)}
                              className="w-full p-4 pl-14 flex items-center justify-between hover:bg-slate-800/20 transition-colors"
                              data-testid={`entry-${entry.id}`}
                            >
                              <span className="text-slate-300">
                                {getEntryDisplayName(category, entry)}
                              </span>
                              <div className="flex items-center gap-2">
                                {files.length > 0 && (
                                  <span className="text-slate-500 text-sm">
                                    {files.length} file{files.length !== 1 ? 's' : ''}
                                  </span>
                                )}
                                {isEntryExpanded ? (
                                  <ChevronUp className="w-4 h-4 text-slate-400" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-slate-400" />
                                )}
                              </div>
                            </button>

                            {/* Entry Details */}
                            {isEntryExpanded && (
                              <div className="px-14 pb-4 space-y-4">
                                {/* Details */}
                                {details.length > 0 && (
                                  <div className="bg-slate-800/30 rounded-lg p-4 space-y-2">
                                    {details.map((field, idx) => (
                                      <div key={idx} className="flex justify-between">
                                        <span className="text-slate-400 text-sm">{field.label}</span>
                                        <span className="text-white text-sm">{field.value}</span>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {/* Files */}
                                {files.length > 0 && (
                                  <div className="space-y-2">
                                    <h4 className="text-slate-400 text-sm font-medium">Documents</h4>
                                    {files.map((file) => (
                                      <div
                                        key={file.id}
                                        className="flex items-center justify-between p-3 bg-slate-800/30 rounded-lg"
                                      >
                                        <div className="flex items-center gap-2">
                                          <FileText className="w-4 h-4 text-amber-400" />
                                          <span className="text-slate-300 text-sm truncate max-w-[200px]">
                                            {file.original_name}
                                          </span>
                                        </div>
                                        <Button
                                          size="sm"
                                          onClick={() => handleDownloadFile(entry.id, file)}
                                          disabled={downloadingFile === file.id}
                                          className="bg-amber-500 hover:bg-amber-400 text-slate-900"
                                          data-testid={`download-file-${file.id}`}
                                        >
                                          {downloadingFile === file.id ? (
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                          ) : (
                                            <>
                                              <Download className="w-4 h-4 mr-1" />
                                              Download
                                            </>
                                          )}
                                        </Button>
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {details.length === 0 && files.length === 0 && (
                                  <p className="text-slate-500 text-sm text-center py-2">
                                    No additional details available
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <p className="text-center text-slate-500 text-sm">
            Powered by <span className="text-amber-400">WillKart.ai</span> • Read-only access
          </p>
        </div>
      </footer>
    </div>
  );
};

export default TrustedDashboard;
